import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { detectPlatform } from "../lib/install";
import InstallModal from "./InstallModal";

/**
 * InstallButton — single, prominent "Get the app" CTA.
 *
 * On click, picks the right path based on platform & whether the browser
 * has offered a native install prompt:
 *   - Android (or any browser firing `beforeinstallprompt`): calls .prompt()
 *   - iOS Safari: opens InstallModal with Share→AddToHomeScreen instructions
 *   - iOS other browsers: opens InstallModal asking the user to switch to Safari
 *   - Desktop: opens InstallModal with copyable link + "open on your phone"
 *
 * The button hides itself entirely if the app is already installed (running
 * in PWA standalone mode), so it never shows up in the installed experience.
 */
export default function InstallButton({ className = "btn-gold", label }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [modalVariant, setModalVariant] = useState(null);

  useEffect(() => {
    if (detectPlatform() === "installed") {
      setInstalled(true);
      return;
    }
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setModalVariant(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    // 1. If the browser has handed us a native prompt, use it.
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch { /* user dismissed — fine */ }
      setDeferredPrompt(null);
      return;
    }
    // 2. Otherwise pick a modal variant based on platform.
    const platform = detectPlatform();
    if (platform === "ios_safari") setModalVariant("ios_safari");
    else if (platform === "ios_in_app") setModalVariant("ios_in_app");
    else if (platform === "ios_other_browser") setModalVariant("ios_other_browser");
    else setModalVariant("desktop_no_prompt"); // android-without-prompt or desktop
  };

  return (
    <>
      <button
        data-testid="install-button"
        onClick={handleClick}
        className={`${className} inline-flex items-center gap-2`}
      >
        <Download size={14} strokeWidth={2} />
        {label || "Get the app"}
      </button>
      {modalVariant && (
        <InstallModal variant={modalVariant} onClose={() => setModalVariant(null)} />
      )}
    </>
  );
}
